import { useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { SeverityBadge } from './SeverityBadge';
import { EvidenceBlock } from './panel/evidence';
import type { ActivityLogDto } from '../types';

interface ActivityDetailDrawerProps {
  entry: ActivityLogDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * If the parsed DetailsJson looks like a `{before, after}` field-level diff,
 * surface it as a table; otherwise return null so the caller renders the
 * raw JSON tree fallback.
 */
function tryRenderDiff(parsed: unknown): { before: Record<string, unknown>; after: Record<string, unknown> } | null {
  if (parsed === null || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  const before = obj.before;
  const after = obj.after;
  if (
    before === null || after === null ||
    typeof before !== 'object' || typeof after !== 'object'
  ) {
    return null;
  }
  return {
    before: before as Record<string, unknown>,
    after: after as Record<string, unknown>,
  };
}

/**
 * One cell of the legacy before/after table.
 *
 * ⚠️ The `JSON.stringify` here is a DELIBERATE exception to E9's "no rendered JSON" rule, and worth naming
 * so it is not mistaken for the thing that rule removed. That rule targets the `<pre>` blob that used to be
 * this drawer's primary detail renderer — a wall of braces standing in for a readable breakdown. This is a
 * one-line fallback for a nested value inside a legacy diff payload, where the alternative is printing
 * `[object Object]` at an auditor. It is reached only by pre-E5 rows carrying a `before`/`after` pair.
 */
function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function ActivityDetailDrawer({ entry, open, onOpenChange }: ActivityDetailDrawerProps) {
  const parsed = useMemo(() => {
    if (!entry?.detailsJson) return null;
    try {
      return JSON.parse(entry.detailsJson) as unknown;
    } catch {
      return null;
    }
  }, [entry?.detailsJson]);

  const diff = useMemo(() => tryRenderDiff(parsed), [parsed]);

  if (!entry) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent />
      </Sheet>
    );
  }

  const diffKeys = diff
    ? Array.from(new Set([...Object.keys(diff.before), ...Object.keys(diff.after)]))
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-start gap-3">
            <SeverityBadge severity={entry.severity} />
            <span>{entry.summary}</span>
          </SheetTitle>
          <SheetDescription className="font-mono text-xs">
            {entry.module}.{entry.actionType} · {new Date(entry.occurredAtUtc).toLocaleString()}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6 px-4 pb-6">
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Subject
            </h3>
            <dl className="grid grid-cols-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Entity</dt>
              <dd className="col-span-2 text-foreground">
                {entry.entityType}
                {entry.entityLabel ? ` · ${entry.entityLabel}` : ''}
              </dd>
              <dt className="text-muted-foreground">ID</dt>
              <dd className="col-span-2 font-mono text-foreground">{entry.entityId}</dd>
            </dl>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Actor
            </h3>
            <dl className="grid grid-cols-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">User</dt>
              <dd className="col-span-2 text-foreground">
                {entry.actorSnapshot}
                {entry.actorUserId !== null && (
                  <span className="ml-2 text-xs text-muted-foreground">(id: {entry.actorUserId})</span>
                )}
              </dd>
              {entry.ipAddress && (
                <>
                  <dt className="text-muted-foreground">IP</dt>
                  <dd className="col-span-2 font-mono text-xs text-foreground">{entry.ipAddress}</dd>
                </>
              )}
              {entry.userAgent && (
                <>
                  <dt className="text-muted-foreground">UA</dt>
                  <dd className="col-span-2 break-all font-mono text-xs text-foreground">
                    {entry.userAgent}
                  </dd>
                </>
              )}
              {entry.correlationId && (
                <>
                  <dt className="text-muted-foreground">Correlation</dt>
                  <dd className="col-span-2 font-mono text-xs text-foreground">
                    {entry.correlationId}
                  </dd>
                </>
              )}
            </dl>
          </section>

          {entry.detailsJson && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Details
              </h3>
              {diff ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field</TableHead>
                      <TableHead>Before</TableHead>
                      <TableHead>After</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {diffKeys.map((key) => (
                      <TableRow key={key}>
                        <TableCell className="font-mono text-xs">{key}</TableCell>
                        <TableCell className="text-xs">{formatCell(diff.before[key])}</TableCell>
                        <TableCell className="text-xs">{formatCell(diff.after[key])}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : entry.evidence.length > 0 ? (
                // The same ready-to-draw blocks the panel renders. This drawer is the admin audit surface,
                // so it gets the readable form too — there is no reason the person with the WIDEST access
                // is the only one shown a raw dump.
                <div className="space-y-2">
                  {entry.evidence.map((block, i) => (
                    <EvidenceBlock key={i} evidence={block} />
                  ))}
                </div>
              ) : (
                // ⚠️ THE `<pre>{JSON.stringify(...)}</pre>` THAT WAS HERE IS GONE. It is the "ugly json" the
                // owner's decision names by name, and it was the ONLY way this system ever showed what a
                // multi-item action actually contained.
                //
                // The bytes are not lost — audit needs them and this is the surface that has the right to
                // them. They are COPYABLE rather than RENDERED, which keeps the payload one click from an
                // auditor without putting a wall of braces in front of everyone else. This branch is
                // reached by a row with no projection entry (everything written before E5) and by a payload
                // the scrub has emptied.
                <div className="flex items-center gap-2 rounded border border-border bg-muted/40 p-3">
                  <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                    No structured detail for this event type — it predates the projection map.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 px-2 text-xs"
                    onClick={() => void navigator.clipboard?.writeText(entry.detailsJson ?? '')}
                  >
                    Copy payload
                  </Button>
                </div>
              )}
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
